import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Modal, Select, Switch } from 'antd';

import RecordBadge from '../common/RecordBadge';
import { CardOptionLabel, cardSearchText } from '../cards/CardVisuals';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useCollectionIdentity from '../../hooks/useCollectionIdentity';
import { normalizeId } from '../../utils/ids';
import { sourceKey } from '../../utils/combinedLists';

/** The most hand-picked cards a list may add (keepr-api: 400 `too_many_sources` past it). */
export const INCLUDE_MAX = 25;

const sameExclude = (a, b) => normalizeId(a.collection) === normalizeId(b.collection) && normalizeId(a.card) === normalizeId(b.card);

function ExcludedRow({ entry, cardsById, onRestore }) {
	const collection = useCollectionIdentity(entry.collection);
	const card = entry.card ? cardsById[normalizeId(entry.card)] : null;
	const name = collection?.name || 'A collection';
	return (
		<li className="combined-edit__row">
			<span className="combined-edit__identity">
				<RecordBadge kind="collection" record={collection || { _id: entry.collection }} title={name} size="xs" />
				{card?.name ? <span className="combined-sources__card">{card.name}</span> : null}
			</span>
			<Switch checked={false} onChange={onRestore} aria-label={`Read ${name} again`} />
		</li>
	);
}

// A source by its collection's badge (named through the session cache when
// the list gave only its id: a refusal's sources) and its card.
function SourceRow({ source, card, handPicked, on, onToggle }) {
	const collection = useCollectionIdentity(source.collection?.name ? source.collection : source.collection?._id);
	const name = collection?.name || 'A collection';
	return (
		<li className="combined-edit__row" data-testid="combined-edit-source">
			<span className="combined-edit__identity">
				<RecordBadge kind="collection" record={collection || source.collection} title={name} size="xs" />
				<span className="combined-sources__card">{card?.name}{handPicked ? ' · added by you' : ''}</span>
			</span>
			<Switch checked={on} onChange={onToggle} aria-label={`Read ${name}`} />
		</li>
	);
}

/**
 * EDIT SOURCES… (KPR-1022; plan §3.3: "leave a source out", "add a
 * hand-picked one"). Every source the list reads, each with a switch: off
 * leaves that (collection, card) out (`exclude`); a card the person added by
 * hand is taken back out of `include` instead. What was left out is listed
 * after, to switch back on. **Add a card…** picks any card the person reads —
 * it joins wherever a collection they list holds it, lined up by element name.
 *
 * Nothing is written until **Save**: `onSubmit({ exclude, include })`. A
 * Modal on a desktop, a bottom sheet on a phone.
 */
export default function EditSourcesModal({ open, isMobile = false, sources = [], definition, onSubmit, onCancel, error = null }) {
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const allCards = useMemo(() => cardDefinitions?.cards || [], [cardDefinitions?.cards]);
	const [exclude, setExclude] = useState([]);
	const [include, setInclude] = useState([]);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!open) return;
		setExclude(definition?.exclude || []);
		setInclude(definition?.include || []);
	}, [open, definition]);

	const readable = sources.filter(source => source?.collection && source.status !== 'unreadable');
	const unreadable = sources.filter(source => source?.status === 'unreadable');
	const sourceCardIds = new Set(readable.map(source => normalizeId(source.card?._id)));
	// A source that is here only because the person added its card by hand is
	// taken out of `include`; any other is left out through `exclude`.
	const pickedAtOpen = useMemo(() => new Set((definition?.include || []).map(normalizeId)), [definition]);
	const handPicked = id => pickedAtOpen.has(normalizeId(id));

	const isOn = (source) => {
		const pair = { collection: source.collection._id, card: source.card?._id };
		if (handPicked(pair.card)) return include.includes(normalizeId(pair.card));
		return !exclude.some(entry => sameExclude(entry, pair) || (!entry.card && normalizeId(entry.collection) === normalizeId(pair.collection)));
	};

	const toggle = (source, on) => {
		const pair = { collection: normalizeId(source.collection._id), card: normalizeId(source.card?._id) };
		if (handPicked(pair.card)) {
			setInclude(prev => (on ? [...new Set([...prev, pair.card])] : prev.filter(id => id !== pair.card)));
			return;
		}
		setExclude(prev => (on
			? prev.filter(entry => !sameExclude(entry, pair) && !(!entry.card && normalizeId(entry.collection) === pair.collection))
			: [...prev.filter(entry => !sameExclude(entry, pair)), pair]));
	};

	const cardOptions = useMemo(() => allCards
		.filter(card => normalizeId(card?._id) && !sourceCardIds.has(normalizeId(card._id)) && !include.includes(normalizeId(card._id)))
		.map(card => ({ value: normalizeId(card._id), label: <CardOptionLabel card={card} />, searchText: cardSearchText(card) })),
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[allCards, include, sources]);

	const pendingIncludes = include.filter(id => !sourceCardIds.has(id) && !unreadable.some(source => normalizeId(source.card?._id) === id));

	const submit = async () => {
		setBusy(true);
		try {
			await onSubmit({ exclude, include });
		} catch {
			// The page shows what went wrong (`error`).
		} finally {
			setBusy(false);
		}
	};

	const body = (
		<div className="combined-edit">
			<ul className="combined-edit__rows" aria-label="Sources">
				{readable.map(source => (
					<SourceRow
						key={sourceKey(source)}
						source={source}
						card={cardsById[normalizeId(source.card?._id)] || source.card}
						handPicked={handPicked(source.card?._id)}
						on={isOn(source)}
						onToggle={on => toggle(source, on)}
					/>
				))}
				{exclude.filter(entry => !readable.some(source => sameExclude(entry, { collection: source.collection._id, card: source.card?._id }))).map(entry => (
					<ExcludedRow
						key={`x:${entry.collection}:${entry.card || ''}`}
						entry={entry}
						cardsById={cardsById}
						onRestore={() => setExclude(prev => prev.filter(other => !sameExclude(other, entry)))}
					/>
				))}
				{pendingIncludes.map(id => (
					<li key={`i:${id}`} className="combined-edit__row">
						<span className="combined-edit__identity">
							<CardOptionLabel card={cardsById[id] || { _id: id, name: 'A card' }} />
						</span>
						<Button type="link" onClick={() => setInclude(prev => prev.filter(other => other !== id))}>Remove</Button>
					</li>
				))}
				{unreadable.map(source => (
					<li key={`u:${source.card?._id}`} className="combined-edit__row">
						<span className="combined-sources__note">A card you added is no longer readable</span>
						<Button type="link" onClick={() => setInclude(prev => prev.filter(other => other !== normalizeId(source.card?._id)))}>Remove</Button>
					</li>
				))}
			</ul>
			<label className="combined-edit__add">
				<span>Add a card</span>
				<Select
					showSearch
					value={null}
					placeholder="Find a card you read"
					options={cardOptions}
					disabled={include.length >= INCLUDE_MAX}
					filterOption={(input, option) => `${option?.searchText || ''}`.toLowerCase().includes(input.toLowerCase())}
					onChange={id => setInclude(prev => (prev.includes(id) ? prev : [...prev, id]))}
					popupMatchSelectWidth={false}
					aria-label="Add a card"
					data-testid="combined-edit-add"
				/>
			</label>
			<p className="combined-list__save-note">
				A card you add joins wherever a collection you have holds it, and lines up with the others by element name.
			</p>
			{error ? <p className="combined-edit__error" role="alert">{error}</p> : null}
		</div>
	);

	if (isMobile) {
		return (
			<Drawer
				placement="bottom"
				height="auto"
				open={open}
				onClose={onCancel}
				title="Edit sources"
				className="combined-edit-sheet"
				destroyOnClose
				extra={<Button type="primary" loading={busy} onClick={submit}>Save</Button>}
			>
				{body}
			</Drawer>
		);
	}
	return (
		<Modal open={open} title="Edit sources" okText="Save" onOk={submit} onCancel={onCancel} confirmLoading={busy} destroyOnClose>
			{body}
		</Modal>
	);
}
